import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { okJson } from '../services/httpError';
import {
  X, FileText, Printer, Download, Loader2,
  Users, Shield, ChevronDown, ChevronRight, RefreshCw,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchWithAuth, condoApi } from '../services/api';
import { getAbsoluteImageUrl } from '../utils/imageUrl';
import { cn } from '../utils/cn';
import { usePorteiroReports } from '../hooks/usePorteiroReports';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportEmployee {
  id: string;
  first_name: string;
  last_name: string;
  position?: string;
  department_name?: string;
  department_id?: string;
  cpf?: string;
  is_active?: boolean;
  identity_photo_url?: string;
  role_metadata?: Record<string, any>;
  roles?: Array<{
    id: string;
    role_type?: string;
    is_active?: boolean;
    role_metadata?: {
      department_id?: string;
      department_name?: string;
      department_ids?: string[];
      departments?: Array<{ id: string; name: string }>;
    };
  }>;
}

interface ReportZone {
  id: string;
  name: string;
  entry_type?: string;
  zone_type?: string;
  device_count?: number;
}

interface AuthorizedPerson {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  role_type?: string;
  department?: string;
  department_name?: string;
  position?: string;
  cpf?: string;
}

type ReportView = 'employees' | 'zones';

interface FuncionariosReportFilters {
  search?: string;
  departmentIds?: string[];
  departmentNames?: string[];
  authorizationStatus?: string;
  noDepartment?: boolean;
}

interface FuncionariosReportProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  customerName?: string;
  initialView?: ReportView;
  /** When set, the report is scoped to the same filters active on the employees list
   *  instead of fetching every employee for the customer. */
  filters?: FuncionariosReportFilters;
}

function buildFilterSummary(filters?: FuncionariosReportFilters): string | undefined {
  if (!filters) return undefined;
  const parts: string[] = [];
  if (filters.search) parts.push(`Busca: "${filters.search}"`);
  if (filters.departmentNames?.length) parts.push(`Departamento(s): ${filters.departmentNames.join(', ')}`);
  if (filters.noDepartment) parts.push('Sem departamento');
  if (filters.authorizationStatus === 'authorized') parts.push('Autorizado');
  if (filters.authorizationStatus === 'pending') parts.push('Pendente de autorização');
  return parts.length ? `Filtros ativos: ${parts.join(' · ')}` : undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function personName(p: AuthorizedPerson): string {
  if (p.name) return p.name;
  return [p.first_name, p.last_name].filter(Boolean).join(' ');
}

function getEmployeeDept(emp: ReportEmployee, deptById: Record<string, string> = {}): string {
  const resolveName = (id: string, name?: string) => name || deptById[id] || null;

  // Mirror the exact logic from Employees.tsx getEmployeeDepartments()
  const activeStaffRoles = (emp.roles ?? []).filter(
    r => r.role_type === 'staff' && r.is_active
  );

  if (activeStaffRoles.length > 0) {
    // New format: first role already aggregates all departments with names
    const firstMeta = activeStaffRoles[0].role_metadata;
    if (firstMeta?.departments && firstMeta.departments.length > 0) {
      return firstMeta.departments.map((d: any) => d.name).join(', ');
    }

    // Collect from all active staff roles (legacy: one role per dept)
    const collected = new Map<string, string>();
    for (const role of activeStaffRoles) {
      const meta = role.role_metadata;
      if (!meta) continue;
      if (meta.department_id) {
        const name = resolveName(meta.department_id, meta.department_name);
        if (name) collected.set(meta.department_id, name);
      }
      if (Array.isArray(meta.department_ids)) {
        meta.department_ids.forEach((id: string) => {
          if (!collected.has(id)) {
            const name = resolveName(id);
            if (name) collected.set(id, name);
          }
        });
      }
    }
    if (collected.size > 0) {
      return Array.from(collected.values()).join(', ');
    }
  }

  // Top-level fallback (legacy field)
  if (emp.department_name) return emp.department_name;

  return 'Sem departamento';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmployeesTable({ employees, deptById }: { employees: ReportEmployee[]; deptById: Record<string, string> }) {
  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Nenhum funcionário encontrado</p>
      </div>
    );
  }

  const sorted = [...employees].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'pt-BR')
  );

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white">
        <tr className="border-b-2 border-gray-200">
          <th className="py-2 px-3 w-9"></th>
          <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Nome</th>
          <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Departamento(s)</th>
          <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">CPF</th>
          <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map(emp => {
          const photoUrl = emp.identity_photo_url ? getAbsoluteImageUrl(emp.identity_photo_url) : undefined;
          const initials = `${emp.first_name?.[0] ?? ''}${emp.last_name?.[0] ?? ''}`.toUpperCase();
          return (
          <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50">
            <td className="py-2 px-3">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-500">
                  {initials || <Users className="h-3 w-3" />}
                </div>
              )}
            </td>
            <td className="py-2.5 px-3 font-medium text-gray-900">
              {emp.first_name} {emp.last_name}
            </td>
            <td className="py-2.5 px-3 text-gray-500 text-xs">{getEmployeeDept(emp, deptById)}</td>
            <td className="py-2.5 px-3 text-gray-400 font-mono text-xs">{emp.cpf ?? '—'}</td>
            <td className="py-2.5 px-3">
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  emp.is_active !== false
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                )}
              >
                {emp.is_active !== false ? 'Ativo' : 'Inativo'}
              </span>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ZonesView({
  zones,
  zoneMembers,
  employeeById,
  deptById,
}: {
  zones: ReportZone[];
  zoneMembers: Record<string, AuthorizedPerson[]>;
  employeeById: Record<string, ReportEmployee>;
  deptById: Record<string, string>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (zones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Shield className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Nenhuma zona encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {zones.map(zone => {
        const members = Array.isArray(zoneMembers[zone.id]) ? zoneMembers[zone.id] : [];
        const staff = members.filter(m => !m.role_type || m.role_type === 'staff');
        const displayMembers = staff.length > 0 ? staff : members;
        const isExpanded = expandedId === zone.id;

        return (
          <div
            key={zone.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
          >
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
              onClick={() => setExpandedId(isExpanded ? null : zone.id)}
            >
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="font-semibold text-gray-900 text-sm">{zone.name}</span>
                {zone.entry_type && (
                  <span className="text-xs bg-gray-100 border border-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                    {zone.entry_type}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs font-semibold px-2.5 py-0.5 rounded-full',
                    displayMembers.length > 0
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-400'
                  )}
                >
                  {displayMembers.length} pessoa{displayMembers.length !== 1 ? 's' : ''}
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                {displayMembers.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    Nenhuma pessoa autorizada nesta zona
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    {displayMembers.map(m => {
                      const emp = employeeById[m.id];
                      const dept = emp
                        ? getEmployeeDept(emp, deptById)
                        : (m.department_name ?? m.department ?? null);
                      const cpf = emp?.cpf ?? m.cpf;
                      return (
                        <div key={m.id} className="flex items-center gap-2 py-1">
                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <Users className="h-3 w-3 text-gray-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {personName(m)}
                            </p>
                            {(dept || cpf) && (
                              <p className="text-xs text-gray-400 truncate">
                                {[dept, cpf ? `CPF: ${cpf}` : null].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Print content (shown only during window.print()) ─────────────────────────

function PrintReport({
  view,
  employees,
  zones,
  zoneMembers,
  employeeById,
  deptById,
  customerName,
  today,
  filterSummary,
}: {
  view: ReportView;
  employees: ReportEmployee[];
  zones: ReportZone[];
  zoneMembers: Record<string, AuthorizedPerson[]>;
  employeeById: Record<string, ReportEmployee>;
  deptById: Record<string, string>;
  customerName?: string;
  today: string;
  filterSummary?: string;
}) {
  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        color: '#111',
        padding: '16mm',
      }}
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          borderBottom: '2px solid #111',
          paddingBottom: '10px',
          marginBottom: '16px',
        }}
      >
        <h1 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
          Relatório de Funcionários
        </h1>
        {customerName && (
          <p style={{ fontSize: '11px', margin: '3px 0 0', color: '#555' }}>{customerName}</p>
        )}
        {filterSummary && (
          <p style={{ fontSize: '10px', margin: '3px 0 0', color: '#777' }}>{filterSummary}</p>
        )}
        <p style={{ fontSize: '9px', margin: '2px 0 0', color: '#888' }}>
          Gerado em {today}
        </p>
      </div>

      {view === 'employees' ? (
        /* Employee table */
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
          <thead>
            <tr style={{ backgroundColor: '#111', color: '#fff' }}>
              {['Nome', 'Departamento(s)', 'CPF', 'Status'].map(h => (
                <th
                  key={h}
                  style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600 }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees
              .sort((a, b) =>
                `${a.first_name} ${a.last_name}`.localeCompare(
                  `${b.first_name} ${b.last_name}`,
                  'pt-BR'
                )
              )
              .map((emp, i) => (
                <tr
                  key={emp.id}
                  style={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : '#fff' }}
                >
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                    {emp.first_name} {emp.last_name}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', color: '#555' }}>
                    {getEmployeeDept(emp, deptById)}
                  </td>
                  <td
                    style={{
                      padding: '4px 8px',
                      borderBottom: '1px solid #eee',
                      fontFamily: 'monospace',
                      color: '#777',
                    }}
                  >
                    {emp.cpf ?? '—'}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                    {emp.is_active !== false ? 'Ativo' : 'Inativo'}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        /* Zones view */
        zones.map(zone => {
          const members = Array.isArray(zoneMembers[zone.id]) ? zoneMembers[zone.id] : [];
          const staff = members.filter(m => !m.role_type || m.role_type === 'staff');
          const display = staff.length > 0 ? staff : members;

          return (
            <div key={zone.id} style={{ marginBottom: '14px', breakInside: 'avoid' }}>
              <div
                style={{
                  backgroundColor: '#f3f4f6',
                  borderLeft: '3px solid #111',
                  padding: '4px 8px',
                  marginBottom: '4px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                }}
              >
                {zone.name}
                {zone.entry_type ? ` (${zone.entry_type})` : ''} — {display.length} pessoa
                {display.length !== 1 ? 's' : ''}
              </div>
              {display.length === 0 ? (
                <p style={{ fontSize: '8px', color: '#aaa', marginLeft: '8px' }}>
                  Nenhuma pessoa autorizada
                </p>
              ) : (
                <table
                  style={{
                    width: 'calc(100% - 8px)',
                    marginLeft: '8px',
                    borderCollapse: 'collapse',
                    fontSize: '8px',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#e5e7eb' }}>
                      {['Nome', 'Departamento(s)', 'CPF'].map(h => (
                        <th key={h} style={{ padding: '3px 6px', textAlign: 'left', color: '#374151' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {display.map((m, i) => {
                      const emp = employeeById[m.id];
                      const dept = emp ? getEmployeeDept(emp, deptById) : (m.department_name ?? m.department ?? '—');
                      const cpf = emp?.cpf ?? m.cpf ?? '—';
                      return (
                        <tr
                          key={m.id}
                          style={{ backgroundColor: i % 2 === 0 ? '#fafafa' : '#fff' }}
                        >
                          <td style={{ padding: '2px 6px', borderBottom: '1px solid #f0f0f0' }}>
                            {personName(m)}
                          </td>
                          <td style={{ padding: '2px 6px', borderBottom: '1px solid #f0f0f0', color: '#666' }}>
                            {dept}
                          </td>
                          <td style={{ padding: '2px 6px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace', color: '#777' }}>
                            {cpf}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FuncionariosReport({
  open,
  onClose,
  customerId,
  customerName,
  initialView = 'employees',
  filters,
}: FuncionariosReportProps) {
  const [view, setView] = useState<ReportView>(initialView);
  const filterSummary = buildFilterSummary(filters);
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState<ReportEmployee[]>([]);
  const [zones, setZones] = useState<ReportZone[]>([]);
  const [zoneMembers, setZoneMembers] = useState<Record<string, AuthorizedPerson[]>>({});
  const [deptMap, setDeptMap] = useState<Record<string, string>>({});

  const employeeById = useMemo(() => {
    const map: Record<string, ReportEmployee> = {};
    employees.forEach(e => { map[e.id] = e; });
    return map;
  }, [employees]);

  const today = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const fetchData = useCallback(async () => {
    if (!customerId || !open) return;
    setIsLoading(true);
    try {
      const [empRes, deptsRes, zonesRes] = await Promise.all([
        condoApi.getEmployees(customerId, {
          page_size: 500,
          sort_by: 'first_name',
          sort_order: 'asc',
          department_ids: filters?.departmentIds?.length ? filters.departmentIds.join(',') : undefined,
          search: filters?.search || undefined,
          authorization_status: filters?.authorizationStatus || undefined,
          no_department: filters?.noDepartment ? 'true' : undefined,
        }),
        condoApi.getDepartments(customerId).catch(() => ({ data: [] })),
        fetchWithAuth(`/api/v1/zones/customer/${customerId}`),
      ]);

      // condoApi returns an axios response; fetchWithAuth returns a fetch Response
      const empData = empRes.data;
      const deptsData = deptsRes.data ?? [];
      // Unchecked, a rejected zones request yielded an error object here and the report was
      // built as though the customer simply had no zones.
      const zonesData = await okJson<any>(zonesRes, 'Erro ao carregar as zonas');

      // Build id → name lookup for departments
      const depts: Array<{ id: string; name: string }> = Array.isArray(deptsData)
        ? deptsData
        : deptsData?.data ?? [];
      const newDeptMap: Record<string, string> = {};
      depts.forEach(d => { newDeptMap[d.id] = d.name; });
      setDeptMap(newDeptMap);

      const empList: ReportEmployee[] =
        empData?.data ?? empData?.employees ?? empData?.items ?? empData ?? [];
      const zoneList: ReportZone[] =
        zonesData?.zones ?? zonesData?.data ?? zonesData ?? [];

      setEmployees(Array.isArray(empList) ? empList : []);
      setZones(Array.isArray(zoneList) ? zoneList : []);

      // Fetch authorized people for each zone in parallel
      const membersMap: Record<string, AuthorizedPerson[]> = {};
      await Promise.all(
        (Array.isArray(zoneList) ? zoneList : []).map(async zone => {
          try {
            const res = await fetchWithAuth(`/api/v1/zones/${zone.id}/authorized-people`);
            const data = await okJson<any>(res, 'Erro ao carregar as pessoas autorizadas');
            const result = data?.people ?? data?.authorized_people ?? data?.data ?? data ?? [];
            membersMap[zone.id] = Array.isArray(result) ? result : [];
          } catch (zoneErr) {
            // Deliberately per-zone tolerant so one bad zone cannot void the whole report, but
            // logged: previously an unchecked body made a failed lookup indistinguishable from
            // a zone that genuinely has nobody authorized.
            console.warn(`[FuncionariosReport] authorized-people failed for zone ${zone.id}:`, zoneErr);
            membersMap[zone.id] = [];
          }
        })
      );
      setZoneMembers(membersMap);
    } catch (e) {
      console.error('[FuncionariosReport] fetch error:', e);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, open, filters]);

  useEffect(() => {
    if (open) {
      setView(initialView);
      fetchData();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();

      // Logo
      try {
        const logoImg = new Image();
        logoImg.src = '/panther-logo.png';
        await new Promise<void>(resolve => {
          logoImg.onload = () => {
            try { doc.addImage(logoImg, 'PNG', 14, 6, 20, 20); } catch { /* skip */ }
            resolve();
          };
          logoImg.onerror = () => resolve();
          setTimeout(() => resolve(), 2000);
        });
      } catch { /* skip */ }

      // Brand
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('ACCESS', 37, 18);

      // Divider
      doc.setLineWidth(0.5);
      doc.line(14, 30, W - 14, 30);

      // Report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Funcionários', 14, 38);

      // Metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const meta = [
        customerName ? `Cliente: ${customerName}` : null,
        `Gerado em: ${today}`,
        `Total: ${employees.length} funcionário${employees.length !== 1 ? 's' : ''}`,
      ].filter(Boolean).join('  |  ');
      doc.text(meta, 14, 44);
      if (filterSummary) {
        doc.setTextColor(120, 120, 120);
        doc.text(filterSummary, 14, 49);
        doc.setTextColor(0, 0, 0);
      }

      if (view === 'employees') {
        const sortedEmployees = [...employees].sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`,
            'pt-BR'
          )
        );

        // Preload each employee's photo (best-effort — a missing/broken photo just
        // leaves that row's avatar cell blank instead of failing the whole export).
        const photosByEmployeeId = new Map<string, HTMLImageElement>();
        await Promise.all(
          sortedEmployees.map(e => {
            if (!e.identity_photo_url) return Promise.resolve();
            return new Promise<void>(resolve => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => { photosByEmployeeId.set(e.id, img); resolve(); };
              img.onerror = () => resolve();
              img.src = getAbsoluteImageUrl(e.identity_photo_url!);
              setTimeout(() => resolve(), 2000);
            });
          })
        );

        autoTable(doc, {
          startY: filterSummary ? 56 : 52,
          head: [['', 'Nome', 'Departamento(s)', 'CPF', 'Status']],
          body: sortedEmployees.map(e => [
            '',
            `${e.first_name} ${e.last_name}`,
            getEmployeeDept(e, deptMap),
            e.cpf ?? '—',
            e.is_active !== false ? 'Ativo' : 'Inativo',
          ]),
          styles: { fontSize: 7, minCellHeight: 10 },
          headStyles: { fillColor: [17, 24, 39] },
          columnStyles: { 0: { cellWidth: 12 } },
          didDrawCell: (data: any) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            const emp = sortedEmployees[data.row.index];
            const img = emp && photosByEmployeeId.get(emp.id);
            if (!img) return;
            const size = 8;
            const x = data.cell.x + (data.cell.width - size) / 2;
            const y = data.cell.y + (data.cell.height - size) / 2;
            try { doc.addImage(img, 'JPEG', x, y, size, size); } catch { /* skip */ }
          },
        });
      } else {
        let y = 52;
        for (const zone of zones) {
          const members = Array.isArray(zoneMembers[zone.id]) ? zoneMembers[zone.id] : [];
          const staff = members.filter(m => !m.role_type || m.role_type === 'staff');
          const display = staff.length > 0 ? staff : members;
          if (display.length === 0) continue;
          if (y > 170) { doc.addPage(); y = 15; }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`${zone.name}${zone.entry_type ? ` (${zone.entry_type})` : ''}`, 14, y);
          y += 5;

          autoTable(doc, {
            startY: y,
            head: [['Funcionário', 'Departamento(s)', 'CPF']],
            body: display.map(m => {
              const emp = employeeById[m.id];
              return [
                personName(m),
                emp ? getEmployeeDept(emp, deptMap) : (m.department_name ?? m.department ?? '—'),
                emp?.cpf ?? m.cpf ?? '—',
              ];
            }),
            styles: { fontSize: 7 },
            headStyles: { fillColor: [55, 65, 81] },
            margin: { left: 14, right: 14 },
          });
          y = (doc as any).lastAutoTable.finalY + 8;
        }
      }

      doc.save(
        `relatorio_funcionarios_${new Date().toISOString().slice(0, 10)}.pdf`
      );
    } catch (e) {
      console.error('[FuncionariosReport] PDF error:', e);
    }
  };

  const canViewReports = usePorteiroReports();
  if (!open || !canViewReports) return null;

  const activeCount = employees.filter(e => e.is_active !== false).length;
  const zonesWithPeople = zones.filter(z => (zoneMembers[z.id] ?? []).length > 0).length;

  return (
    <>
      {/* Print CSS */}
      <style>{`
        @media print {
          body > *:not(#funcionarios-print-root) { display: none !important; }
          #funcionarios-print-root { display: block !important; }
        }
        #funcionarios-print-root { display: none; }
      `}</style>

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col"
          style={{ maxHeight: '90vh' }}
        >
          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gray-900 flex items-center justify-center">
                <FileText className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Relatório de Funcionários
                </h2>
                <p className="text-xs text-gray-500">
                  {customerName && `${customerName} · `}
                  {today}
                </p>
                {filterSummary && (
                  <p className="text-xs text-blue-600 mt-0.5">{filterSummary}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                disabled={isLoading}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                PDF
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                <Printer className="h-3.5 w-3.5" />
                Imprimir
              </button>
              <button
                onClick={onClose}
                className="ml-1 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Stats + Tabs */}
          <div className="px-6 pt-4 pb-0 flex-shrink-0">
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                {
                  label: 'Total de Funcionários',
                  value: employees.length,
                  sub: 'cadastrados',
                  color: 'text-gray-900',
                },
                {
                  label: 'Ativos',
                  value: activeCount,
                  sub: 'com acesso habilitado',
                  color: 'text-green-600',
                },
                {
                  label: 'Zonas com Acesso',
                  value: zonesWithPeople,
                  sub: `de ${zones.length} zonas`,
                  color: 'text-blue-600',
                },
              ].map(({ label, value, sub, color }) => (
                <div
                  key={label}
                  className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3"
                >
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium">
                    {label}
                  </p>
                  <p className={cn('text-2xl font-bold tabular-nums mt-0.5', color)}>
                    {isLoading ? '—' : value}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-gray-200">
              {[
                { key: 'employees' as ReportView, label: 'Lista de Funcionários', icon: Users },
                { key: 'zones' as ReportView, label: 'Acesso por Zona', icon: Shield },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                    view === key
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Carregando dados do relatório...</span>
              </div>
            ) : view === 'employees' ? (
              <EmployeesTable employees={employees} deptById={deptMap} />
            ) : (
              <ZonesView zones={zones} zoneMembers={zoneMembers} employeeById={employeeById} deptById={deptMap} />
            )}
          </div>
        </div>
      </div>

      {/* Print-only content */}
      <div id="funcionarios-print-root">
        <PrintReport
          view={view}
          employees={employees}
          zones={zones}
          zoneMembers={zoneMembers}
          employeeById={employeeById}
          deptById={deptMap}
          customerName={customerName}
          today={today}
          filterSummary={filterSummary}
        />
      </div>
    </>
  );
}
